/**
 * @file Authentication — login/logout, session persistence
 */

import { getClient } from '@state/supabase.js';
import { sbPullAll, setUserId } from '@state/store.js';
import { emit } from '@core/events.js';
import { toast } from '@components/toast.js';

let _currentUser = null;

/**
 * Check for existing session on app startup.
 * @returns {Promise<boolean>} true if user is authenticated
 */
export async function checkSession() {
  const sb = getClient();
  try {
    const { data: { session } } = await sb.auth.getSession();
    if (session && session.user) {
      _currentUser = session.user;
      setUserId(session.user.id);
      showApp();
      await sbPullAll();
      emit('auth:login', _currentUser);
      return true;
    }
  } catch (e) {
    console.warn('[auth] Session check failed:', e.message);
  }
  showLogin();
  return false;
}

/**
 * Sign in with email and password.
 */
export async function doLogin(email, password) {
  const sb = getClient();
  const errEl = document.getElementById('login-error');
  const btn = document.getElementById('login-btn');

  if (btn) { btn.disabled = true; btn.textContent = 'Signing in...'; }
  if (errEl) errEl.textContent = '';

  try {
    const { data, error } = await sb.auth.signInWithPassword({ email, password });
    if (error) {
      if (errEl) errEl.textContent = error.message;
      if (btn) { btn.disabled = false; btn.textContent = 'Sign In'; }
      return false;
    }
    _currentUser = data.user;
    setUserId(data.user.id);
    showApp();
    await sbPullAll();
    emit('auth:login', _currentUser);
    toast('Signed in successfully', 'success');
    return true;
  } catch (e) {
    if (errEl) errEl.textContent = 'Connection error. Check your network.';
    if (btn) { btn.disabled = false; btn.textContent = 'Sign In'; }
    return false;
  }
}

/**
 * Sign out and clear local data.
 */
export async function doLogout() {
  const sb = getClient();
  try {
    await sb.auth.signOut();
  } catch (e) {
    console.warn('[auth] Sign out error:', e.message);
  }
  _currentUser = null;
  setUserId(null);

  // Clear pbf_ keys from localStorage
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith('pbf_')) keysToRemove.push(k);
  }
  keysToRemove.forEach(k => { try { localStorage.removeItem(k); } catch (e) { /* no-op */ } });

  showLogin();
  emit('auth:logout');
}

/**
 * Listen for auth state changes (token refresh).
 */
export function onAuthStateChange() {
  const sb = getClient();
  sb.auth.onAuthStateChange((event, session) => {
    if (event === 'TOKEN_REFRESHED' && session) {
      _currentUser = session.user;
    }
    if (event === 'SIGNED_OUT') {
      _currentUser = null;
      showLogin();
    }
  });
}

/**
 * Get the current user object.
 */
export function getCurrentUser() {
  return _currentUser;
}

/**
 * Wire up the login form submit handler.
 */
export function initLoginForm() {
  const form = document.getElementById('login-form');
  if (!form) return;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = document.getElementById('login-email').value.trim();
    const password = document.getElementById('login-password').value;
    if (!email || !password) return;
    await doLogin(email, password);
  });
}

// ── UI helpers ──

function showApp() {
  const login = document.getElementById('login-screen');
  const app = document.getElementById('app');
  if (login) login.classList.add('hidden');
  if (app) app.classList.remove('hidden');
}

function showLogin() {
  const login = document.getElementById('login-screen');
  const app = document.getElementById('app');
  if (login) login.classList.remove('hidden');
  if (app) app.classList.add('hidden');
  // Reset form
  const form = document.getElementById('login-form');
  if (form) form.reset();
  const err = document.getElementById('login-error');
  if (err) err.textContent = '';
  const btn = document.getElementById('login-btn');
  if (btn) { btn.disabled = false; btn.textContent = 'Sign In'; }
}
